import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Search, Plus, Users } from "lucide-react";
import api from "@/lib/api";

export default function ChatList() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");

  const fetchConversations = () => {
    api.get("/chat/conversations").then((r) => setConversations(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchConversations(); const iv = setInterval(fetchConversations, 5000); return () => clearInterval(iv); }, []);

  useEffect(() => {
    if (showNewChat) api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, [showNewChat]);

  const filtered = search
    ? conversations.filter((c) => c.customer_name.toLowerCase().includes(search.toLowerCase()) || c.customer_email.toLowerCase().includes(search.toLowerCase()))
    : conversations;

  const existingIds = new Set(conversations.map((c) => c.customer_id));
  const newChatCustomers = useMemo(() => {
    const list = customers.filter((c) => !existingIds.has(c.id));
    if (!customerSearch) return list.slice(0, 10);
    return list.filter((c) => c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.email.toLowerCase().includes(customerSearch.toLowerCase())).slice(0, 10);
  }, [customers, customerSearch, existingIds]);

  const startNewChat = (customerId) => {
    navigate(`/crm/chat/${customerId}`);
  };

  return (
    <div data-testid="chat-list-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="crm-heading">Suporte / Chat</h1>
        <button onClick={() => setShowNewChat(!showNewChat)} className="crm-btn-primary" data-testid="new-chat-btn">
          <Plus className="w-4 h-4" /> Nova Conversa
        </button>
      </div>

      {/* New chat modal */}
      {showNewChat && (
        <div className="crm-card p-5 mb-6" data-testid="new-chat-panel">
          <h3 className="crm-subheading flex items-center gap-2"><Users className="w-4 h-4" style={{ color: "var(--crm-accent)" }} /> Iniciar conversa com cliente</h3>
          <div className="relative mb-3">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
            <input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)}
              className="crm-input pl-10" placeholder="Buscar cliente por nome ou email..." data-testid="new-chat-search" />
          </div>
          <div className="space-y-1 max-h-[250px] overflow-y-auto">
            {newChatCustomers.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: "var(--crm-text-dim)" }}>
                {customerSearch ? "Nenhum cliente encontrado" : "Todos os clientes já possuem conversas"}
              </p>
            ) : (
              newChatCustomers.map((c) => (
                <button key={c.id} onClick={() => startNewChat(c.id)} data-testid={`new-chat-customer-${c.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors"
                  style={{ background: "transparent" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "var(--crm-surface-hover)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
                    {c.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "var(--crm-text)" }}>{c.name}</p>
                    <p className="text-xs truncate" style={{ color: "var(--crm-text-muted)" }}>{c.email}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="chat-search"
          placeholder="Filtrar conversas..." className="crm-input pl-10" />
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 crm-card animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="crm-card p-12 text-center" style={{ color: "var(--crm-text-dim)" }}>
          <MessageCircle className="w-8 h-8 mx-auto mb-3" style={{ opacity: 0.3 }} />
          <p className="text-sm">Nenhuma conversa encontrada</p>
          <p className="text-xs mt-1">Clique em "Nova Conversa" para iniciar um atendimento</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <Link key={c.customer_id} to={`/crm/chat/${c.customer_id}`} data-testid={`chat-convo-${c.customer_id}`}
              className="crm-card p-4 flex items-center gap-4 transition-all block"
              style={{ borderLeft: c.unread > 0 ? "3px solid var(--crm-accent)" : "3px solid transparent" }}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                style={{ background: c.unread > 0 ? "var(--crm-accent)" : "var(--crm-accent-subtle)", color: c.unread > 0 ? "white" : "var(--crm-accent)" }}>
                {c.customer_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold truncate" style={{ color: "var(--crm-text)" }}>{c.customer_name}</p>
                  <span className="text-[10px] flex-shrink-0 ml-2" style={{ color: "var(--crm-text-dim)" }}>
                    {new Date(c.last_time).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-xs truncate" style={{ color: c.unread > 0 ? "var(--crm-text-secondary)" : "var(--crm-text-dim)" }}>
                    {c.last_sender === "admin" ? "Você: " : ""}{c.last_message}
                  </p>
                  {c.unread > 0 && (
                    <span className="ml-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                      style={{ background: "var(--crm-accent)", color: "white" }}>{c.unread}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
